// @ts-check
/**
 * Marker Comment Docking Panel Component
 * Shows marker comments and allows replies in a docking panel anchored to the marker
 */

import { Logger } from "../utils/Logger.js";
import "./style.css";

const supportedFileTypes = "image/*";

export class MarkerCommentPanel extends window.Autodesk.Viewing.UI
	.DockingPanel {
	constructor(viewer, id, title, options) {
		// Enable movable functionality in options
		const panelOptions = {
			...options,
			movable: true,
		};

		super(viewer.container, id, title, panelOptions);

		this.viewer = viewer;
		this.logger = Logger.createLogger("MarkerCommentPanel");
		this.currentMarker = null;
		this.onReplyCallback = null;
		this.onCloseCallback = null;
		this.attachedFiles = []; // Store attached files as binary data

		// Load CSS styles
		this.loadStyles();

		// Initialize the panel content
		this.initializeContent();

		// Initialize movable functionality
		this.initializeMovable();

		// Style the panel
		this.container.style.width = "380px";
		this.container.style.height = "auto";
		this.container.style.maxHeight = "400px";
		this.container.style.resize = "none";
		this.container.style.overflow = "hidden";
		this.container.style.backgroundColor = "#ffffff";
		this.container.style.border = "none";
		this.container.style.borderRadius = "12px";
		this.container.style.boxShadow = "0px 0px 0px 1px #f5f5f5";
		this.apiActions = options.apiActions || null;
	}

	/**
	 * Load CSS styles for the panel
	 */
	loadStyles() {
		// Check if styles are already loaded
		if (document.getElementById("marker-comment-panel-styles")) {
			return;
		}

		const link = document.createElement("link");
		link.id = "marker-comment-panel-styles";
		link.rel = "stylesheet";
		link.type = "text/css";

		// In a real application, you would use the actual path to your CSS file
		// For now, we'll inject the styles directly
		const style = document.createElement("style");
		style.id = "marker-comment-panel-styles";

		document.head.appendChild(style);
	}

	/**
	 * Initialize the panel content structure
	 */
	initializeContent() {
		this.container.innerHTML = `
      <div class="marker-comment-panel-content">
        <div class="marker-comment-header">
          <div class="marker-comment-header-left">
            <h3>0001039</h3>
          </div>
          <div class="marker-comment-header-right">
            <div class="marker-comment-status-badge">
              <div class="marker-comment-status-text">Pending</div>
            </div>
            <button class="marker-comment-close-btn" title="Close">&times;</button>
          </div>
        </div>

        <div class="marker-comment-body">
          <div class="marker-comment-content-wrapper">
            <div class="marker-comment-replies-container" id="marker-comments-list">
              <!-- Comments will be populated here -->
            </div>
          </div>
        </div>

        <div class="marker-comment-reply-section">
          <div class="marker-comment-reply-form">
            <div class="marker-comment-reply-input-row">
              <textarea
                class="marker-comment-reply-input"
                placeholder="Add comment..."
              ></textarea>
              <div class="marker-comment-reply-actions">
                <div class="marker-comment-action-group">
                  <button class="marker-comment-action-btn" title="Attach file">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5C12.38 2.5 13.5 3.62 13.5 5V15.5C13.5 16.05 13.05 16.5 12.5 16.5C11.95 16.5 11.5 16.05 11.5 15.5V6H10V15.5C10 16.88 11.12 18 12.5 18C13.88 18 15 16.88 15 15.5V5C15 2.79 13.21 1 11 1C8.79 1 7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6H16.5Z" fill="#737373"/>
                    </svg>
                  </button>
                </div>
                <button class="marker-comment-send-btn btn--primary" title="Send">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.75 0.203145L0.749985 7.70002C-0.325016 8.32189 -0.221891 9.91252 0.92811 10.3844L4.49999 11.8625V14.5C4.49999 15.95 6.34999 16.5469 7.20624 15.3938L8.575 13.5469L12.0719 14.9906C12.2562 15.0656 12.45 15.1031 12.6438 15.1031C12.9 15.1031 13.1531 15.0375 13.3813 14.9094C13.7813 14.6844 14.0563 14.2844 14.1281 13.8313L15.9844 1.73127C16.175 0.478145 14.8313 -0.41873 13.75 0.203145ZM5.99999 14.5V12.4813L7.14374 12.9531L5.99999 14.5ZM12.6438 13.6031L7.83749 11.6188L12.2188 5.2969C12.5531 4.81252 11.9219 4.25002 11.4781 4.6344L4.86874 10.3938L1.49999 9.00002L14.5 1.50002L12.6438 13.6031Z" fill="#FFF"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
		this.bindEvents();
	}

	/**
	 * Bind event listeners
	 */
	bindEvents() {
		const closeBtn = this.container.querySelector(".marker-comment-close-btn");
		const replyInput = this.container.querySelector(
			".marker-comment-reply-input"
		);
		const sendBtn = this.container.querySelector(".marker-comment-send-btn");
		const attachBtn = this.container.querySelector(
			".marker-comment-action-btn"
		);

		// Close button
		closeBtn.addEventListener("click", () => {
			this.close();
		});

		// Reply button
		sendBtn.addEventListener("click", () => {
			const replyText = replyInput.value.trim();
			if (
				(replyText || this.attachedFiles.length > 0) &&
				this.onReplyCallback
			) {
				// Include attached files in the reply data
				const replyData = {
					text: replyText,
					attachedFiles:
						this.attachedFiles.length > 0 ? this.attachedFiles : undefined,
				};

				this.onReplyCallback(replyData);
				replyInput.value = "";
				this.clearAttachedFiles();
				sendBtn.disabled = false;
			}
		});

		// Attach file button
		attachBtn.addEventListener("click", () => {
			// Open file picker or handle attachment logic
			const fileInput = document.createElement("input");
			fileInput.type = "file";
			fileInput.accept = supportedFileTypes;
			fileInput.onchange = async (e) => {
				const target = e.target;
				if (
					target instanceof HTMLInputElement &&
					target.files &&
					target.files.length > 0
				) {
					const file = target.files[0];
					try {
						// Convert file to binary data
						const arrayBuffer = await this.fileToArrayBuffer(file);
						const binaryData = Array.from(new Uint8Array(arrayBuffer));

						// Create file metadata object
						const fileMetadata = {
							name: file.name,
							size: file.size,
							type: file.type,
							data: binaryData,
							lastModified: file.lastModified,
						};

						// Add to attached files
						this.attachedFiles.push(fileMetadata);

						// Update UI to show attached file
						this.updateAttachedFilesUI();

						// Enable send button after attachment
						sendBtn.disabled = false;
					} catch (error) {
						console.error("Error processing attached file:", error);
						// Show error to user (you might want to add a proper notification system)
						alert("Error processing file. Please try again.");
					}
				}
			};
			fileInput.click();
		});

		// Keyboard shortcuts
		replyInput.addEventListener("keydown", (e) => {
			if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault();
				sendBtn.click();
			} else if (e.key === "Escape") {
				e.preventDefault();
				this.close();
			}
		});

		// Auto-resize textarea
		replyInput.addEventListener("input", () => {
			replyInput.style.height = "auto";
			replyInput.style.height = Math.min(replyInput.scrollHeight, 120) + "px";
		});
	}

	/**
	 * Initialize movable functionality for the panel
	 */
	initializeMovable() {
		// Set the header as the drag handle for moving the panel
		const header = this.container.querySelector(".marker-comment-header");
		if (header) {
			// Use the built-in Autodesk Viewer method to make panel movable
			// The header becomes the drag handle
			this.initializeMoveHandlers(header);

			// Prevent close button and other interactive elements from triggering drag
			const closeBtn = this.container.querySelector(
				".marker-comment-close-btn"
			);

			if (closeBtn) {
				closeBtn.addEventListener("mousedown", (e) => {
					e.stopPropagation();
				});
			}
		}
	}

	/**
	 * Show the panel for a new marker
	 * @returns {Promise<Object|null>} Comment data or null if cancelled
	 */
	createNewMarker(marker) {
		return new Promise((resolve) => {
			this.currentMarker = null;

			// Clear any previously attached files
			this.clearAttachedFiles();

			// Reset header title
			const headerTitle = this.container.querySelector(
				".marker-comment-header h3"
			);
			if (headerTitle) {
				headerTitle.textContent = "New comment";
			}

			// Reset content
			const commentsList = this.container.querySelector(
				"#marker-comments-list"
			);
			if (commentsList) {
				commentsList.innerHTML = "";
			}

			// Store original callbacks
			const originalOnReplyCallback = this.onReplyCallback;
			const originalOnCloseCallback = this.onCloseCallback;

			// Set up temporary callbacks for new marker creation
			this.onReplyCallback = (replyData) => {
				// Restore original callbacks
				this.onReplyCallback = originalOnReplyCallback;
				this.onCloseCallback = originalOnCloseCallback;

				// Hide the panel
				this.setVisible(false);

				// Resolve with the comment data
				resolve(replyData);
			};

			this.onCloseCallback = () => {
				// Restore original callbacks
				this.onReplyCallback = originalOnReplyCallback;
				this.onCloseCallback = originalOnCloseCallback;

				// Resolve with null (cancelled)
				resolve(null);
			};

			// Show panel
			this.setVisible(true);
			this.positionNearMarker(marker);

			// Focus on the input field
			const replyInput = this.container.querySelector(
				".marker-comment-reply-input"
			);
			if (replyInput) {
				setTimeout(() => replyInput.focus(), 100);
			}
		});
	}

	/**
	 * Show the panel for a specific marker
	 * @param {Object} marker - Marker data
	 * @param {Function} onReply - Callback when reply is submitted
	 * @param {Function} onClose - Callback when panel is closed
	 */
	showForMarker(marker, onReply, onClose) {
		this.currentMarker = marker;
		this.onReplyCallback = onReply;
		this.onCloseCallback = onClose;

		// Clear any previously attached files
		this.clearAttachedFiles();

		// Update the marker ID in header
		const headerTitle = this.container.querySelector(
			".marker-comment-header h3"
		);
		if (headerTitle) {
			headerTitle.textContent = `#${marker.id}` || "unknown id";
		}

		// Update content
		this.updateContent(marker);

		// Position panel relative to marker
		this.positionNearMarker(marker);

		// Show panel
		this.setVisible(true);
	}

	/**
	 * Update panel content with marker data
	 * @param {Object} marker - Marker data
	 */
	updateContent(marker) {
		const commentsList = this.container.querySelector("#marker-comments-list");

		if (!commentsList) return;

		// Create all comments (main comment + replies) in chronological order
		const allComments = [];

		// Add main comment
		if (marker.comment && marker.comment.trim()) {
			allComments.push({
				text: marker.comment,
				user: marker.userName || "Unknown User",
				timestamp: marker.timestamp,
				isMainComment: true,
				files: marker.metadata?.files || [],
				userImageLink: marker.userImageLink || "",
			});
		}

		// Add replies
		if (marker.replies && marker.replies.length > 0) {
			allComments.push(
				...marker.replies.map((reply) => {
					return {
						...reply,
						user: reply.userName || "Unknown User",
						timestamp: reply.timestamp,
						text: reply.comment || "",
						isMainComment: false,
						files: reply.metadata?.files || [],
						userImageLink: reply.userImageLink || "",
					};
				})
			);
		}

		// Sort by timestamp
		allComments.sort((a, b) => a.timestamp - b.timestamp);

		if (allComments.length === 0) {
			commentsList.innerHTML = `
        <div class="marker-comment-empty-state">
          No comments yet. Be the first to comment!
        </div>
      `;
			return;
		}

		// Render all comments
		commentsList.innerHTML = allComments
			.map((comment, commentIndex) => {
				const avatarLetter = this.getUserAvatar(comment.user);
				const userImageLink =
					comment.userImageLink && comment.userImageLink.trim() !== ""
						? comment.userImageLink
						: null;
				const attachedFiles = comment.files || [];

				return `
        <div class="marker-comment-item">
          <div class="marker-comment-item-content">
            <div class="marker-comment-item-header">
              <div class="marker-comment-author-info">
                <div class="marker-comment-author-details">
                  <div class="marker-comment-avatar">${userImageLink ? `<img src="${this.escapeHtml(userImageLink)}" alt="${this.escapeHtml(comment.user)}" />` : avatarLetter}</div>
                  <div class="marker-comment-author">${this.escapeHtml(comment.user)}</div>
                </div>
                <div class="marker-comment-time">${this.formatTime(comment.timestamp)}</div>
              </div>
            </div>
            <div class="marker-comment-text-content">
              <div class="marker-comment-text">${this.formatCommentText(comment.text)}</div>
              ${comment.image ? `<img class="marker-comment-image" src="${comment.image}" alt="Comment attachment" />` : ""}
              ${
								attachedFiles.length > 0
									? `
                <div class="marker-comment-attachments-display">
                  <div class="marker-comment-attachments-header">Attachments (${attachedFiles.length})</div>
                  <div class="marker-comment-attachments-list">
                    ${attachedFiles
											.map((file, fileIndex) => {
												if (file.type && file.type.startsWith("image/")) {
													// Create blob URL for image rendering
													const uint8Array = new Uint8Array(file.data);
													const blob = new Blob([uint8Array], {
														type: file.type,
													});
													const imageUrl = URL.createObjectURL(blob);

													// Render image with download button on hover
													return `
                          <div class="marker-comment-attachment-item-display marker-comment-image-wrapper">
                            <img class="marker-comment-image"
                                 src="${imageUrl}"
                                 alt="${this.escapeHtml(file.name)}"
                                 onload="this.style.display='block'"
                                 onerror="this.style.display='none'; console.error('Failed to load image:', '${this.escapeHtml(file.name)}')" />
                            <button class="marker-comment-download-btn marker-comment-image-download-btn"
                              data-comment-index="${commentIndex}"
                              data-file-index="${fileIndex}"
                              title="Download ${this.escapeHtml(file.name)}">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97933 19.8043 4.588 19.413C4.19667 19.0217 4.00067 18.5507 4 18V15H6V18H18V15H20V18C20 18.55 19.8043 19.021 19.413 19.412C19.0217 19.803 18.5507 19.9993 18 20H6Z" fill="currentColor"/>
                              </svg>
                            </button>
                          </div>
                        `;
												} else {
													// Non-image file
													return `
                          <div class="marker-comment-attachment-item-display">
                            <div class="marker-comment-attachment-info">
                              <span class="marker-comment-attachment-name">${this.escapeHtml(file.name)}</span>
                              <span class="marker-comment-attachment-size">(${this.formatFileSize(file.size)})</span>
                            </div>
                            <button class="marker-comment-download-btn"
                              data-comment-index="${commentIndex}"
                              data-file-index="${fileIndex}"
                              title="Download ${this.escapeHtml(file.name)}">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97933 19.8043 4.588 19.413C4.19667 19.0217 4.00067 18.5507 4 18V15H6V18H18V15H20V18C20 18.55 19.8043 19.021 19.413 19.412C19.0217 19.803 18.5507 19.9993 18 20H6Z" fill="currentColor"/>
                              </svg>
                            </button>
                          </div>
                        `;
												}
											})
											.join("")}
                  </div>
                </div>
              `
									: ""
							}
            </div>
          </div>
        </div>
      `;
			})
			.join("");

		// Add event listeners for download buttons
		const downloadButtons = commentsList.querySelectorAll(
			".marker-comment-download-btn"
		);
		downloadButtons.forEach((button) => {
			button.addEventListener("click", (e) => {
				const target = e.target.closest(".marker-comment-download-btn");
				if (target instanceof HTMLElement) {
					const commentIndexStr = target.getAttribute("data-comment-index");
					const fileIndexStr = target.getAttribute("data-file-index");

					if (commentIndexStr && fileIndexStr) {
						const commentIndex = parseInt(commentIndexStr);
						const fileIndex = parseInt(fileIndexStr);

						if (commentIndex >= 0 && commentIndex < allComments.length) {
							const comment = allComments[commentIndex];
							const files = comment.files || [];

							if (fileIndex >= 0 && fileIndex < files.length) {
								this.downloadFile(files[fileIndex]);
							}
						}
					}
				}
			});
		});

		// Scroll to bottom to show latest comments
		setTimeout(() => {
			commentsList.scrollTop = commentsList.scrollHeight;
		}, 100);
	}

	/**
	 * Format comment text with mentions highlighted
	 * @param {string} text - Comment text
	 * @returns {string} Formatted HTML
	 */
	formatCommentText(text) {
		if (!text) return "";

		// Simple mention detection and highlighting
		return this.escapeHtml(text).replace(
			/\b(John|@\w+)\b/g,
			'<span class="marker-comment-mention">$1</span>'
		);
	}

	/**
	 * Get user avatar (first letter of name)
	 * @param {string} userName - User name
	 * @returns {string} Avatar letter
	 */
	getUserAvatar(userName) {
		if (!userName || userName.trim() === "") {
			return "?";
		}
		return userName.trim().charAt(0).toUpperCase();
	}

	/**
	 * Format timestamp for display
	 * @param {number} timestamp - Unix timestamp
	 * @returns {string} Formatted time
	 */
	formatTime(timestamp) {
		if (!timestamp) return "";

		const date = new Date(timestamp);
		// Format: Aug. 12, 2025 (month short uppercase + period)
		const months = [
			"Jan ",
			"Feb ",
			"Mar ",
			"Apr ",
			"May ",
			"Jun ",
			"Jul ",
			"Aug ",
			"Sep ",
			"Oct ",
			"Nov ",
			"Dec ",
		];
		const monthStr = months[date.getMonth()];
		const day = date.getDate();
		const year = date.getFullYear();
		return `${monthStr} ${day}, ${year}`;
	}

	/**
	 * Position panel near the marker element
	 * @param {Object} marker - Marker data
	 */
	positionNearMarker(marker) {
		if (!marker.element) return;

		const markerRect = marker.element.getBoundingClientRect();
		const viewerRect = this.viewer.container.getBoundingClientRect();

		// Calculate position relative to viewer container
		const left = markerRect.right + 10; // 10px offset from marker
		const top = markerRect.top;

		// Ensure panel stays within viewer bounds
		const panelWidth = 350;
		const panelHeight = 500;

		let adjustedLeft = left;
		let adjustedTop = top;

		// Adjust horizontal position if panel would overflow
		if (left + panelWidth > viewerRect.width) {
			adjustedLeft = markerRect.left - panelWidth - 10; // Position to the left of marker
		}

		// Adjust vertical position if panel would overflow
		if (top + panelHeight > viewerRect.height) {
			adjustedTop = viewerRect.height - panelHeight - 20;
		}

		// Make sure panel doesn't go above viewport
		if (adjustedTop < 20) {
			adjustedTop = 20;
		}

		// @ts-ignore - container provided by Autodesk DockingPanel
		this.container.style.left = `${Math.max(10, adjustedLeft)}px`;
		// @ts-ignore - container provided by Autodesk DockingPanel
		this.container.style.top = `${Math.max(10, adjustedTop)}px`;
	}

	/**
	 * Close the panel
	 */
	close() {
		// @ts-ignore - setVisible inherited from DockingPanel
		this.setVisible(false);

		if (this.onCloseCallback) {
			this.onCloseCallback();
		}

		// Clear attached files when closing
		this.clearAttachedFiles();

		this.currentMarker = null;
		this.onReplyCallback = null;
		this.onCloseCallback = null;
	}

	/**
	 * Add a new reply to the current marker and send to API
	 * @param {Object} replyData - Reply data {text, attachedFiles}
	 * @param {Object} [options] - Optional params: { fileId, onSuccess, onError }
	 */
	addReply(replyData, options = {}) {
		if (!this.currentMarker) return;

		// Prevent duplicate: only add to UI after API success
		const { fileId } = options;
		const commentId = this.currentMarker.id;

		const files = replyData.attachedFiles || [];

		// Keep a copy of files so we can attach them on success (API may not echo them back)
		this._pendingReplyFiles = files;

		if (fileId && this.apiActions) {
			// Prepare API payload
			const apiPayload = {
				comment: replyData.text || "",
			};

			if (files.length > 0) {
				apiPayload.metadata = {};

				apiPayload.metadata.files = files.map((file) => ({
					name: file.name,
					size: file.size,
					type: file.type,
					data: file.data,
					lastModified: file.lastModified,
				}));
			}

			this.apiActions.replyToBuildingModelComment(
				commentId,
				apiPayload,
				fileId,
				this.onReplySuccess.bind(this)
			);
		}
	}

	onReplySuccess(reply) {
		if (!this.currentMarker) return;

		// Add reply to current marker
		const replyUser = reply.commentedByUser || {};
		const replyUserName =
			`${replyUser.firstName || ""} ${replyUser.lastName || ""}`.trim() ||
			replyUser.firstName ||
			this.currentMarker.userName ||
			"Unknown User";
		const replyUserImage =
			replyUser.imageLink || this.currentMarker.userImageLink || "";

		const files = reply.metadata?.files || this._pendingReplyFiles || [];

		this.currentMarker.replies.push({
			user: reply.commentedByUserId || replyUser.id || null,
			userName: replyUserName,
			userImageLink: replyUserImage,
			comment: reply.comment,
			timestamp: reply.createdAt,
			metadata: { files: files },
		});

		// Clear pending files after attaching
		this._pendingReplyFiles = null;

		// Update UI to show the new reply
		this.updateContent(this.currentMarker);
	}

	/**
	 * Escape HTML to prevent XSS
	 * @param {string} text - Text to escape
	 * @returns {string} Escaped text
	 */
	escapeHtml(text) {
		const div = document.createElement("div");
		div.textContent = text;
		return div.innerHTML;
	}

	/**
	 * Check if panel is currently visible
	 * @returns {boolean} True if visible
	 */
	isVisible() {
		// @ts-ignore - container provided by Autodesk DockingPanel
		return this.container.style.display !== "none";
	}

	/**
	 * Convert File object to ArrayBuffer
	 * @param {File} file - File to convert
	 * @returns {Promise<ArrayBuffer>} File as ArrayBuffer
	 */
	fileToArrayBuffer(file) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				if (reader.result instanceof ArrayBuffer) {
					resolve(reader.result);
				} else {
					reject(new Error("Failed to read file as ArrayBuffer"));
				}
			};
			reader.onerror = reject;
			reader.readAsArrayBuffer(file);
		});
	}

	/**
	 * Download a file from its binary data
	 * @param {Object} file - File object with name, type, and data properties
	 */
	downloadFile(file) {
		try {
			// Convert binary data array back to Uint8Array
			const uint8Array = new Uint8Array(file.data);

			// Create blob from binary data
			const blob = new Blob([uint8Array], {
				type: file.type || "application/octet-stream",
			});

			// Create download URL
			const url = URL.createObjectURL(blob);

			// Create temporary link element and trigger download
			const link = document.createElement("a");
			link.href = url;
			link.download = file.name || "download";

			// Append to body, click, and remove
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Clean up URL object
			URL.revokeObjectURL(url);

			console.log("File downloaded:", file.name);
		} catch (error) {
			console.error("Error downloading file:", error);
			alert("Error downloading file. Please try again.");
		}
	}

	/**
	 * Update UI to show attached files
	 */
	updateAttachedFilesUI() {
		// @ts-ignore - container provided by Autodesk DockingPanel
		const replySection = this.container.querySelector(
			".marker-comment-reply-section"
		);
		if (!replySection) return;

		// Remove existing attachments UI
		const existingAttachments = replySection.querySelector(
			".marker-comment-attachments"
		);
		if (existingAttachments) {
			existingAttachments.remove();
		}

		// Add attachments UI if there are files
		if (this.attachedFiles.length > 0) {
			const attachmentsContainer = document.createElement("div");
			attachmentsContainer.className = "marker-comment-attachments";

			attachmentsContainer.innerHTML = `
        <div class="marker-comment-attachments-header">
          <span>Attached Files (${this.attachedFiles.length})</span>
        </div>
        <div class="marker-comment-attachments-list">
          ${this.attachedFiles
						.map(
							(file, index) => `
            <div class="marker-comment-attachment-item">
              <span class="marker-comment-attachment-name">${this.escapeHtml(file.name)}</span>
              <span class="marker-comment-attachment-size">(${this.formatFileSize(file.size)})</span>
              <button class="marker-comment-attachment-remove" data-index="${index}" title="Remove">×</button>
            </div>
          `
						)
						.join("")}
        </div>
      `;

			// Add event listeners for remove buttons
			const removeButtons = attachmentsContainer.querySelectorAll(
				".marker-comment-attachment-remove"
			);
			removeButtons.forEach((button) => {
				button.addEventListener("click", (e) => {
					const target = e.target;
					if (target instanceof HTMLElement) {
						const indexStr = target.getAttribute("data-index");
						if (indexStr) {
							const index = parseInt(indexStr);
							this.removeAttachedFile(index);
						}
					}
				});
			});

			// Insert before the reply form
			const replyForm = replySection.querySelector(
				".marker-comment-reply-form"
			);
			if (replyForm) {
				replySection.insertBefore(attachmentsContainer, replyForm);
			}
		}
	}

	/**
	 * Remove an attached file by index
	 * @param {number} index - Index of file to remove
	 */
	removeAttachedFile(index) {
		if (index >= 0 && index < this.attachedFiles.length) {
			this.attachedFiles.splice(index, 1);
			this.updateAttachedFilesUI();
		}
	}

	/**
	 * Clear all attached files
	 */
	clearAttachedFiles() {
		this.attachedFiles = [];
		this.updateAttachedFilesUI();
	}

	/**
	 * Format file size for display
	 * @param {number} bytes - File size in bytes
	 * @returns {string} Formatted size
	 */
	formatFileSize(bytes) {
		if (bytes === 0) return "0 B";
		const k = 1024;
		const sizes = ["B", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
	}
}
